import { Injectable, inject } from "@angular/core";
import { environment } from "../../../environments/environment";
import { forkJoin, from, Observable, of, firstValueFrom } from "rxjs";
import { catchError, map, mergeMap, retry, tap, timeout } from "rxjs/operators";
import Swal from "sweetalert2";

import { LoggerService } from "./logger.service";
import { PcsApiService } from "./pcs-api.service";
import { FormattingService } from "./formatting.service";
import { VcloudHttpClientService } from "./vcloud-http-client.service";
import { VcloudCostTrackerService } from "./vcloud-cost-tracker.service";
import { VcloudDebugCaptureService } from "./vcloud-debug-capture.service";
import { VcloudFiltersApiService } from "./vcloud-filters-api.service";

import { Currency } from "../models/currency";
import {
  CloudProvider,
  FilterableL1,
  FilterableL2,
  FiltersType,
  GroupByPayload,
  Level,
  QueryFilterSimple,
  QueryMetadata,
  TableId,
} from "../models/vcloud-api";
import { Filter, FilterValue } from "../models/filtering";
import { CSP } from "../models/csp-api";
import { CustomReport, ReportShare } from "../models/report-sharing";

import { ROWS_PER_PAGE } from "../constant/vcloud-api";
import { MAX_EXPORT_DURATION, MAX_EXPORT_PARALLELISM } from "../constant/export";
import { CLOUD_PROVIDERS_ALL } from "../constant/llm";
import { FILTERS_API_ENDPOINTS } from "./service.constants";
import { TableColumn } from "../models/common-table";

const SECOND: number = 1000;

type ShareLinkFilters = { all: Array<{ any: Array<{ eq: [string, string] }> }> };
type ShareCodeResponse = { DisplayName?: string; Filters: ShareLinkFilters };

const CSP_LEVELS: Record<CSP, Array<{ id: string; label?: string }>> = {
  aws: [
    { id: "account_id" },
    { id: "account_name" },
    { id: "status", label: "Account Status" },
    { id: "local_market", label: "Master Account" },
    { id: "managed_by" },
    { id: "business_service" },
    { id: "environment" },
    { id: "vams_id" },
    { id: "business_email" },
    { id: "confidentiality" },
    { id: "technical_email" },
    { id: "sec_assessment", label: "Security Assessment ID" },
  ],
  gcp: [
    { id: "project_id" },
    { id: "po_number" },
    { id: "project_name" },
    { id: "project_number" },
    { id: "business_service" },
    { id: "cost_centre" },
    { id: "wbs" },
    { id: "programme" },
    { id: "sec_assessment", label: "Security Assessment ID" },
    { id: "project_owner", label: "Business Owner" },
    { id: "data_governance_compliance_manager", label: "Data Governance & Compliance Manager" },
    { id: "managed_by" },
    { id: "confidentiality" },
    { id: "environment" },
    { id: "local_market" },
    { id: "budget_owner", label: "Cost Centre Owner" },
    { id: "status", label: "Project Status" },
    { id: "bu", label: "Business Unit" },
  ],
  oci: [
    { id: "compartment_name" },
    { id: "compartment_id" },
    { id: "compartment_status" },
    { id: "parent_id" },
    { id: "parent_type" },
    { id: "budget_owner" },
    { id: "compartment_owner" },
    { id: "programme" },
    { id: "ownership_type" },
    { id: "technical_owner" },
    { id: "confidentiality" },
    { id: "cost_centre" },
    { id: "service_class" },
    { id: "portfolio" },
    { id: "managed_by" },
    { id: "local_market" },
  ],
  drcc: [
    { id: "compartment_name" },
    { id: "compartment_id" },
    { id: "compartment_description" },
    { id: "tenant_name" },
    { id: "tenant_id" },
    { id: "budget_owner" },
    { id: "created_by" },
    { id: "home_region" },
    { id: "compartment_status" },
    { id: "parent_name" },
    { id: "parent_id" },
    { id: "business_service" },
    { id: "compartment_owner" },
    { id: "confidentiality" },
    { id: "compartment_type" },
    { id: "environment" },
    { id: "local_market" },
    { id: "managed_by" },
    { id: "portfolio" },
    { id: "programme" },
    { id: "service_class" },
    { id: "technical_owner" },
    { id: "compartment_category" },
    { id: "request_id" },
  ],
  azure: [
    { id: "programme" },
    { id: "cost_centre" },
    { id: "budget_owner" },
    { id: "status", label: "Subscription Status" },
    { id: "group" },
    { id: "enrollment_account" },
    { id: "subscription_owner" },
    { id: "subscription_name" },
    { id: "subscription_id" },
    { id: "managed_by" },
    { id: "technical_owner" },
    { id: "vams_id" },
    { id: "environment" },
  ],
};

@Injectable({
  providedIn: "root",
})
export class VCloudSmartApiService {
  private readonly formatting = inject(FormattingService);
  private readonly log = inject(LoggerService);
  private readonly pcsApi = inject(PcsApiService);
  private readonly httpClient = inject(VcloudHttpClientService);
  private readonly costTracker = inject(VcloudCostTrackerService);
  private readonly debugCapture = inject(VcloudDebugCaptureService);
  private readonly filtersApi = inject(VcloudFiltersApiService);

  private readonly equivalentIdSets: Array<Set<string>> = [
    new Set(["line_item_usage_account_id", "account_id"]),
    new Set(["awsAccountId", "account_id"]),
    new Set(["AccountId", "account_id"]),
    new Set(["product_compartmentId", "compartment_id"]),
    new Set(["cost_subscriptionId", "subscription_id"]),
    new Set(["subId", "subscription_id"]),
    new Set(["prop_subId", "subscription_id"]),
    new Set(["subscriptionId", "subscription_id"]),
  ];

  public readonly months: string[] = this.getMonths(); // DO NOT USE, use https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleDateString

  public getCustomReports(): Observable<CustomReport[]> {
    return this.pcsApi.get<{ Resources: CustomReport[] }>("tenancies-v1/share-codes").pipe(
      map((r) => {
        return (r?.Resources ?? []).filter((r: CustomReport) => {
          const t: string = (r as ReportShare)?.Target || "";
          return !t.includes("/recently-visited/");
        });
      }),
      map((reports: CustomReport[]) => {
        const deduplicatedReports: CustomReport[] = [];
        for (const r of reports) {
          if (
            !deduplicatedReports.some(({ DisplayName, Filters, ...rest }) => {
              return (
                (rest as ReportShare).Target === (r as ReportShare)?.Target &&
                Filters === r?.Filters &&
                DisplayName === r?.DisplayName
              );
            })
          ) {
            deduplicatedReports.push(r);
          }
        }
        return deduplicatedReports;
      }),
    );
  }

  public processAsyncDownloadRequestBody(body: GroupByPayload): GroupByPayload {
    const clone: GroupByPayload = JSON.parse(JSON.stringify(body));
    if (clone?.Limit !== undefined) {
      delete clone.Limit;
    }
    if (clone?.Offset !== undefined) {
      delete clone.Offset;
    }
    if (clone?.OrderBy !== undefined) {
      delete clone.OrderBy;
    }
    if (clone?.SortOrder !== undefined) {
      delete clone.SortOrder;
    }
    return clone;
  }

  public preProcessRequestFilters(allFilters: (QueryFilterSimple | FilterableL2)[]): QueryFilterSimple[] {
    const simplifiedFilters: (FilterableL1 | FilterableL2 | QueryFilterSimple)[] = allFilters
      .filter((f) => (f as FilterableL2).Filters === undefined)
      .map((f) => this.filtersApi.simplifyFilter(f as QueryFilterSimple));

    const deduplicatedSimplifedFilters: (FilterableL1 | FilterableL2 | QueryFilterSimple)[] =
      this.filtersApi.deduplicateFilters(simplifiedFilters);

    const orderedSimpleFilters: (FilterableL1 | FilterableL2 | QueryFilterSimple)[] = deduplicatedSimplifedFilters
      .filter(
        (f) =>
          (f as QueryFilterSimple).Type === FiltersType.date || (f as QueryFilterSimple).Type === FiltersType.timestamp,
      )
      .concat(
        this.filtersApi.orderFilters(
          deduplicatedSimplifedFilters.filter(
            (f) =>
              (f as QueryFilterSimple).Type !== FiltersType.date &&
              (f as QueryFilterSimple).Type !== FiltersType.timestamp,
          ) as QueryFilterSimple[],
        ),
      );

    const complexFilters: (FilterableL1 | FilterableL2 | QueryFilterSimple)[] = allFilters.filter(
      (f) => (f as FilterableL2).Filters !== undefined,
    );

    return orderedSimpleFilters.concat(complexFilters) as QueryFilterSimple[];
  }

  public getLevelsForMetadata(csp: CSP): Level[] {
    return (CSP_LEVELS[csp] ?? []).map((entry) => ({ ...entry, table: "Table_2" }));
  }

  // only to be used with CostDaily data store
  public get azureFilters(): Filter[] {
    return this.filtersApi.getAzureFilters((date, months, dayNumber) => this.addMonths(date, months, dayNumber));
  }

  public decodeGCPEmail(email: string): string {
    return this.formatting.decodeGCPEmail(email);
  }

  /**
   * This helper function will convert shareId={"service": ["EC2, "ECS", ...]} into filters
   *
   * It's an alternative to using the API-based shareId code for applying filters.
   *
   * For example, you can use it to generate links to other reports in VCloudSmart where some filters are pre-applied.
   */
  public extractFiltersFromShareId(shareId: string): {
    Filters: { all: Array<{ any: Array<{ eq: [string, string] }> }> };
  } {
    return this.filtersApi.extractFiltersFromShareId(shareId);
  }

  private async groupByGetAllHelper<T extends string | number>(
    { Limit, Offset, ...body }: GroupByPayload,
    pageSize: number,
    exportDuration: number = MAX_EXPORT_DURATION,
    parallelism: number = MAX_EXPORT_PARALLELISM,
  ): Promise<{ Results: { Group: string[]; Value: T[] }[] }> {
    let offset: number = 0;
    let allRows: Array<{ Group: string[]; Value: T[] }> = [];
    const startTime: number = new Date().getTime();
    while (true) {
      this.log.info("processing next batch for export...");
      const tasks: Array<Promise<{ Results: { Group: string[]; Value: T[] }[]; Metadata?: QueryMetadata }>> = [];
      for (let i = 0; i < parallelism; i++) {
        this.log.info({ task: i, offset, pageSize });
        tasks.push(firstValueFrom(this.groupBy<T>({ ...body, Limit: pageSize, Offset: offset })));
        offset += pageSize;
      }
      let lastResponse: { Results: { Group: string[]; Value: T[] }[]; Metadata?: QueryMetadata };
      for (const t of tasks) {
        let response: { Results: { Group: string[]; Value: T[] }[]; Metadata?: QueryMetadata };
        try {
          response = await t;
        } catch (e) {
          this.log.error(e);
          await Swal.fire({
            title: "Error",
            html: `An error has been encountered while exporting data. Retrieved only the first ${allRows.length} rows.`,
            icon: "error",
          });
          return { ...lastResponse, Results: allRows };
        }
        allRows = allRows.concat(response?.Results ?? []);
        lastResponse = response;
        this.log.info({ lastResponse, allRows, response });
      }
      const now: number = new Date().getTime();
      const timeExceeded: boolean = now - startTime >= exportDuration;
      const noMoreData: boolean = (lastResponse?.Results?.length ?? 0) < pageSize;
      this.log.info({ now, startTime, timeExceeded, noMoreData });
      if (noMoreData || timeExceeded) {
        if (timeExceeded) {
          await Swal.fire({
            title: "Warning",
            html: `You have reached the limit of ${Math.ceil(
              exportDuration / SECOND,
            )} seconds. Retrieved only the first ${allRows.length} rows.`,
            icon: "warning",
          });
        }
        return { ...lastResponse, Results: allRows };
      }
    }
  }
  public getFilters(
    mergeFilters: Filter[],
    payload: Partial<GroupByPayload> & { CloudProvider: CloudProvider; DataStore: string },
    shareId: string,
    tryRecallPreviousFilters: boolean = true,
    isSharedReport: boolean = false,
  ): Observable<QueryFilterSimple[]> {
    const shareRequest$ = shareId
      ? shareId.startsWith("{")
        ? of(this.extractFiltersFromShareId(shareId))
        : this.pcsApi.get<ShareCodeResponse>(`tenancies-v1/share-codes/${shareId}`).pipe(
            catchError((e) => {
              this.log.error(e);
              return of({ Filters: { all: [] } as ShareLinkFilters });
            }),
          )
      : of({ Filters: { all: [] } as ShareLinkFilters });
    // page components need to wait for this request
    // and apply the filters to the initial requests used to populate the page
    return shareRequest$.pipe(
      mergeMap((share) => {
        try {
          if (typeof share?.Filters === "string") {
            share.Filters = JSON.parse(share?.Filters);
          }
        } catch (e) {
          this.log.error(e);
          share.Filters = { all: [] };
        }
        if ((share?.Filters?.all?.length ?? 0) === 0) {
          return this.injectFilterValues(mergeFilters, payload, isSharedReport).pipe(
            map((result) => {
              this.log.info({ msg: "injected filter values", result, share, mergeFilters, payload, shareId });
              return this.filtersApi.convertToBqFilters(mergeFilters);
            }),
          );
        } else {
          this.filtersApi.applyShareLinkFilters(share, mergeFilters);
          this.log.info({ msg: "applied share link to filters", share, mergeFilters, payload, shareId });
          return this.handleFiltersChange(mergeFilters, payload, true).pipe(map(({ vCloudFilters }) => vCloudFilters));
        }
      }),
      map((filters: QueryFilterSimple[]) => {
        const segments: string[] = location.pathname
          .split("/")
          .filter(Boolean)
          .map((s: string) => s.toLowerCase());
        const hashSegments: string[] = ((location?.hash || "").split("#").filter(Boolean)?.[0] || "")
          .split("/")
          .filter(Boolean);
        const csp: CloudProvider = CLOUD_PROVIDERS_ALL.find((csp: CloudProvider) => {
          return segments.includes(csp) || hashSegments.includes(csp);
        });
        let recalledSome: boolean = false;
        if (csp && !shareId && tryRecallPreviousFilters) {
          const previousFilters: QueryFilterSimple[] = this.filtersApi.tryRecallPreviousFilters(csp);
          if (previousFilters.length > 0) {
            for (const f of mergeFilters) {
              if ((f.selected?.size ?? 0) !== 0) {
                continue;
              }
              const fDetails: { table: TableId | undefined; id: string } = this.filtersApi.getTableForFilter(f);
              for (const pf of previousFilters) {
                const pfDetails: { table: TableId | undefined; id: string } =
                  this.filtersApi.getTableForQueryFilter(pf);
                const idSet: Set<string> = new Set([fDetails.id, pfDetails.id]);
                const equivalent: boolean =
                  fDetails.id === pfDetails.id ||
                  this.equivalentIdSets.some((s) => this.filtersApi.setEquals(idSet, s));

                if (!equivalent) {
                  continue;
                }
                const previousValues: FilterValue[] = Array.isArray(pf.Value) ? pf.Value : [pf.Value];
                for (const pfVal of previousValues) {
                  this.log.info({
                    msg: "recalled filter based on previous usage",
                    filter: f,
                    previousFilters: pf,
                    value: pfVal,
                  });
                  if (!f.values.includes(pfVal)) {
                    f.values = [pfVal, ...(f.values || [])].sort();
                  }
                  f.selected.add(pfVal);
                  recalledSome = true;
                }
              }
            }
          }
        }
        if (recalledSome) {
          this.filtersApi.openSnackBar("Recalled previously used filters");
          return this.filtersApi.convertToBqFilters(mergeFilters, true);
        }
        return filters;
      }),
    );
  }

  public handleFiltersChange(
    filters: Filter[],
    { Filters, ...requestBody }: GroupByPayload,
    isRecallingFilters: boolean = false,
  ): Observable<{
    vCloudFilters: QueryFilterSimple[];
    filters: Filter[];
  }> {
    const isSharedReport: boolean = !!Object.fromEntries(
      location.search
        .replace("?", "")
        .split("&")
        .map((pair) => pair.split("=")),
    )?.shareId;

    if (
      !isRecallingFilters &&
      (location?.pathname || "").startsWith("/vcloudsmart/v2/") &&
      (location?.search || "").includes("shareId=")
    ) {
      isRecallingFilters = true;
    }

    // Store current filter state before modification
    const bqFilters: QueryFilterSimple[] = this.filtersApi.convertToBqFilters(
      filters,
      isRecallingFilters,
      isSharedReport,
    );

    for (const f of filters) {
      if (!f.datePickerRange && f?.selected?.size === 0) {
        f.values = [];
      }
    }

    return this.getFilters(
      filters,
      {
        ...requestBody,
        Filters: ((Filters ?? []) as QueryFilterSimple[]).concat(bqFilters),
      },
      null,
      false,
      isSharedReport,
    ).pipe(
      map(() => {
        return {
          filters,
          vCloudFilters: this.filtersApi.convertToBqFilters(filters, true, isSharedReport),
        };
      }),
    );
  }

  private injectFilterValues(
    mergeFilters: Filter[],
    {
      OrderBy,
      Projection,
      Limit,
      SortOrder,
      Distinct,
      ..._payload
    }: Partial<GroupByPayload> & { CloudProvider: CloudProvider; DataStore: string },
    isSharedReport: boolean = false,
  ): Observable<Array<any>> {
    return forkJoin(
      mergeFilters
        .filter(({ datePickerRange, values }) => !datePickerRange && (values ?? []).length === 0)
        .map((f) => {
          const injectedFilters: QueryFilterSimple[] = this.filtersApi.convertToBqFilters(
            mergeFilters.filter((m) => m !== f),
            false,
            isSharedReport,
          );
          const payload: GroupByPayload = {
            ...JSON.parse(JSON.stringify(_payload)),
            // inject date filters (if any)
            Filters: JSON.parse(JSON.stringify(_payload?.Filters ?? [])).concat(injectedFilters),
            Limit: f.getAll ? undefined : ROWS_PER_PAGE,
            Projection: [
              {
                Attribute: f.table ? `${f.table}.${f.id}` : f.id,
              },
            ],
            Distinct: true,
          };

          // inject date filters (if any)
          payload.Filters = ((payload.Filters as QueryFilterSimple[]) ?? []).concat(
            this.filtersApi.convertToBqFilters(mergeFilters, false, isSharedReport),
          );

          // optimisations
          if (
            payload.SelfJoin === undefined &&
            (payload.SelfJoins?.length ?? 0) === 0 &&
            (!Array.isArray(payload.Filters) || payload.Filters.length === 0) &&
            (!Array.isArray(payload.GroupBy) || payload.GroupBy.length === 0) &&
            (!Array.isArray(payload.GroupByV2) || payload.GroupByV2.length === 0)
          ) {
            if (f.table === "Table_1") {
              // no need to join to another table
              delete payload.Join;
            } else if (f.table === "Table_2" && payload.Join) {
              // if joining to another table but only getting the column from the other table,
              // then there is no need to query the first table at all
              payload.DataStore = payload.Join.DataStore;
              payload.Projection = payload.Projection.map(({ Attribute, ...rest }) => ({
                Attribute: Attribute.replace(/^Table_2/, "Table_1"),
                ...rest,
              }));
              delete payload.Join;
            }
          }

          if (payload?.Join?.Type !== "LEFT" && payload?.Join?.DataStore === "CloudResourcesMetadata") {
            payload.Join.Type = "LEFT";
          }

          const ops = [
            map((response: { Results: { Group: string[]; Value: string[] }[]; Metadata: QueryMetadata }) => {
              const newValues: string[] = (response?.Results ?? [])
                .filter(({ Value }) => Array.isArray(Value))
                .map(({ Value }) => Value[0]);
              f.values = [...new Set((f.values ?? []).concat(newValues))].sort();
              this.log.info({ msg: "injected filter value", filter: f });
            }),
            catchError((e) => {
              this.log.error(e);
              return of(undefined);
            }),
          ];

          if (f.getAll) {
            // @ts-ignore
            return this.groupByGetAll<string>(payload).pipe(...ops);
          } else {
            // @ts-ignore
            return this.groupBy<string>(payload).pipe(...ops);
          }
        }),
    ).pipe(
      catchError((e) => {
        this.log.error(e);
        return of([]);
      }),
    );
  }
  /**
   * WARNING:
   *
   * this should only be used in exceptional circumstances,
   * it will paginate through the entire content of the table and return ALL rows.
   *
   * Good page value sizes are between 2000 and 5000.
   */
  public groupByGetAll<T extends string | number>(
    body: GroupByPayload,
    pageSize = ROWS_PER_PAGE,
    exportDuration = MAX_EXPORT_DURATION,
    parallelism = MAX_EXPORT_PARALLELISM,
  ): Observable<{ Results: { Group: string[]; Value: T[] }[] }> {
    return from(this.groupByGetAllHelper<T>(body, pageSize, exportDuration, parallelism));
  }
  public groupByAsync(body: GroupByPayload, currency?: Currency, columns?: TableColumn[]): Observable<void> {
    const requestBody: GroupByPayload = JSON.parse(this.JSONStringifyStable(body));

    if (requestBody.Offset) {
      delete requestBody.Offset;
    }

    if (requestBody.Limit) {
      delete requestBody.Limit;
    }

    requestBody.Filters = this.filtersApi.preProcessRequestFilters(requestBody?.Filters ?? []);

    const url: string = location.href.replace(location.origin, "");

    const segments: string[] = url
      .split("/")
      .filter(Boolean)
      .filter((s) => s !== "v2" && s !== "vcloudsmart");

    return this.httpClient.post(FILTERS_API_ENDPOINTS.GROUP_BY, {
      ...requestBody,
      Async: true,
      Metadata: Object.fromEntries(
        Object.entries({
          Url: url,
          CloudProvider: location.pathname
            .split("/")
            .map((s) => s.trim().toLowerCase())
            .find((segment) => CLOUD_PROVIDERS_ALL.concat("evo" as CloudProvider).includes(segment as CloudProvider)),
          CurrencyExchangeRate: currency?.exchangeRate,
          CurrencyId: currency?.symbol,
          Section: segments[1],
          Page: segments[2],
          Columns: columns?.filter((c) => !c.isCalculated).map((c) => c.label),
        }).filter((e) => !!e[1]),
      ),
    });
  }

  private preProcessResponseBody<T>(Results: { Group: string[]; Value: T[] }[]) {
    const resultsSafe: { Group: string[]; Value: T[] }[] = Results ?? [];
    for (const r of resultsSafe) {
      for (let i = 0; i < (r?.Value?.length ?? 0); i++) {
        // @ts-ignore
        if (r?.Value[i] === -0) {
          // @ts-ignore
          r.Value[i] = 0;
        }
      }
      for (let i = 0; i < (r?.Group?.length ?? 0); i++) {
        // @ts-ignore
        if (r?.Group[i] === -0) {
          // @ts-ignore
          r.Group[i] = 0;
        }
      }
    }
  }

  public groupBy<T extends number | string>(
    body: GroupByPayload,
  ): Observable<{ Results: { Group: string[]; Value: T[] }[]; Metadata?: QueryMetadata }> {
    const requestBody: GroupByPayload = JSON.parse(this.JSONStringifyStable(body));

    // to facilitate caching on the front-end and back-end, we standardise the request body format below
    this.filtersApi.standardiseRequestBody(requestBody);
    this.filtersApi.preProcessFilters(requestBody);

    const request$: Observable<{ Results: { Group: string[]; Value: T[] }[]; Metadata?: QueryMetadata }> =
      this.httpClient.post(FILTERS_API_ENDPOINTS.GROUP_BY, requestBody);

    return request$.pipe(
      timeout(30 * 1000),
      retry(2),
      tap(({ Results }) => {
        this.preProcessResponseBody<T>(Results);
      }),
      tap(({ Metadata }) => {
        if (!environment.production) {
          setTimeout(() => {
            this.costTracker.handleQueryCost({ Metadata: { job_id: `bq-${Metadata.job_id}`, cost: Metadata.cost } });
          }, 2000);
        }
      }),
      tap((response) =>
        this.debugCapture.captureResponse(response, body, (payload) => this.JSONStringifyStable(payload)),
      ),
    );
  }
  public JSONStringifyStable(obj: GroupByPayload): string {
    const allKeys: Set<string> = new Set<string>();
    obj = this.filtersApi.sortFilters(obj);

    JSON.stringify(obj, (key, value) => (allKeys.add(key), value));
    return JSON.stringify(obj, Array.from(allKeys).sort());
  }

  public addDays(date: number | string | Date, days: number): Date {
    const result: Date = new Date(date);
    if (days !== 0) {
      result.setDate(result.getDate() + days);
    }
    return result;
  }

  private getMonths(): Array<string> {
    const year: number = new Date().getFullYear();
    const locales: string = navigator.language || "en-GB";
    const options: Intl.DateTimeFormatOptions = { month: "short" as "short" };
    return Array(12)
      .fill(null)
      .map((_, i) =>
        new Date(`${year}-${(i + 1).toString().padStart(2, "0")}-01`).toLocaleDateString(locales, options),
      );
  }

  public addMonths(_date: number | string | Date, months: number, dayNumber: number = 1): Date {
    // https://stackoverflow.com/questions/2706125/javascript-function-to-add-x-months-to-a-date#2706169
    let date: Date = new Date(_date);
    let m: number,
      d: number = (date = new Date(+date)).getDate();
    date.setMonth(date.getMonth() + months, 1);
    m = date.getMonth();
    date.setDate(d);
    if (date.getMonth() !== m) {
      date.setDate(0);
    }
    date.setDate(dayNumber);
    return date;
  }
}
